import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Stack,
  Autocomplete,
  TextField,
  Typography,
  Paper,
  Divider,
  useTheme,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "src/context/auth";
import {
  fetchSmsHistory,
  SmsHistoryItem,
  sendSmsDirect,
  markSmsAsRead,
} from "src/api/sms-history";
import { getRoutes } from "src/utilities/routes";
import { IAutoCompleteLabelValue } from "src/interface/enum/GeneralType";
import { IAppUser } from "src/interface/IUser";
import { TypeUser } from "src/interface/enum/TypeUser";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";

type SmsHistoryProps = {
  defaultCustomerRef?: string;
  defaultPhone?: string;
  hideFilters?: boolean;
};

const SmsHistoryPage = ({
  defaultCustomerRef,
  defaultPhone,
  hideFilters,
}: SmsHistoryProps) => {
  const { user } = useAuth();
  const theme = useTheme();
  const Routes = getRoutes();
  const queryClient = useQueryClient();

  const [phone, setPhone] = useState(defaultPhone ?? "");
  const [customerRef, setCustomerRef] = useState(defaultCustomerRef ?? "");
  const [outboxText, setOutboxText] = useState("");

  useEffect(() => {
    if (defaultCustomerRef) {
      setCustomerRef(defaultCustomerRef);
    }
  }, [defaultCustomerRef]);

  useEffect(() => {
    if (defaultPhone) {
      setPhone(defaultPhone);
    }
  }, [defaultPhone]);
  const { data: customersData, isLoading: isCustomersLoading } = useQuery({
    queryKey: ["customers-parent-only"],
    queryFn: async () => {
      const response = await fetch(
        Routes.UsersRoutes + "getByRole/" + TypeUser.CUSTOMER,
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
            Accept: "application/json",
          },
        }
      );
      if (!response.ok) {
        throw new Error("خطا در دریافت لیست مشتریان");
      }
      return (await response.json()) as IAppUser[];
    },
    enabled: !!user?.token,
    staleTime: 5 * 60 * 1000,
  });

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["sms-history", phone, customerRef],
    queryFn: () =>
      fetchSmsHistory(user?.token || "", {
        phone: defaultPhone || phone, // اگر defaultPhone تنظیم شده باشد، از آن استفاده می‌کنیم
        customerRef: defaultPhone
          ? defaultCustomerRef || customerRef
          : customerRef, // اگر defaultPhone تنظیم شده باشد، از defaultCustomerRef استفاده می‌کنیم
        page: 0,
        pageSize: 200,
      }),
    enabled: !!user?.token,
  });

  const messages = useMemo(() => {
    const list = data?.items || [];
    console.log("sms-history items", list);
    return list;
  }, [data]);
  const statusLabel = (s?: string | null) => {
    if (!s) return "";
    const key = s.toLowerCase();
    if (key === "sent") return "ارسال شد";
    if (key === "failed") return "ناموفق";
    if (key === "skipped") return "رد شد";
    if (key === "cancelled" || key === "canceled") return "لغو شد";
    if (key === "queued") return "در صف";
    return s;
  };
  const customerOptions: IAutoCompleteLabelValue[] = useMemo(() => {
    return (
      customersData
        ?.filter((c) => !c.parentRef)
        .map((item) => ({
          label:
            `${item.fName ?? ""} ${item.lName ?? ""} ${item.coName ?? ""}`.trim() ||
            item.phoneNumber ||
            "",
          value: item.id,
          item,
        })) || []
    );
  }, [customersData]);

  const selectedCustomer = useMemo(
    () => customerOptions.find((c) => c.value === customerRef) || null,
    [customerOptions, customerRef]
  );

  const latestPhone = useMemo(() => {
    if (phone) return phone;
    if (messages.length > 0) return messages[0].phoneNumber;
    return "";
  }, [messages, phone]);

  // پیدا کردن customerRef از روی شماره موبایل
  const customerRefByPhone = useMemo(() => {
    if (!latestPhone || !customersData) return null;
    const customer = customersData.find((c) => c.phoneNumber === latestPhone);
    return customer?.id || null;
  }, [latestPhone, customersData]);

  const sendMutation = useMutation({
    mutationFn: async () => {
      if (!latestPhone) throw new Error("شماره موبایل یافت نشد");
      if (!outboxText.trim()) throw new Error("متن پیامک خالی است");

      // استفاده از customerRef، defaultCustomerRef یا customerRefByPhone
      const finalCustomerRef =
        customerRef || defaultCustomerRef || customerRefByPhone || "";
      if (!finalCustomerRef) throw new Error("شناسه مشتری یافت نشد");

      await sendSmsDirect(user?.token || "", {
        phoneNumber: latestPhone,
        customerRef: finalCustomerRef,
        message: outboxText.trim(),
      });
    },
    onSuccess: () => {
      setOutboxText("");
      refetch();
    },
  });

  const markAsReadMutation = useMutation({
    mutationFn: async (smsId: number) => {
      await markSmsAsRead(user?.token || "", smsId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sms-history"] });
      refetch();
    },
  });

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "100%",
      }}
    >
      <Typography variant="h6">تاریخچه پیامک</Typography>
      {!hideFilters && (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <TextField
            label="شماره موبایل"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                const entered = (e.target as HTMLInputElement)?.value ?? "";
                setPhone(entered);
              }
            }}
            fullWidth
          />
          <Autocomplete
            options={customerOptions}
            loading={isCustomersLoading}
            value={selectedCustomer}
            onChange={(_, newValue) => {
              const val = newValue?.value ?? "";
              setCustomerRef(val);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="مشتری"
                placeholder="جستجو و انتخاب مشتری"
              />
            )}
            fullWidth
          />
        </Stack>
      )}

      <Divider />

      {/* کادر ارسال پیام جدید در بالا */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          gap: 1,
          flexDirection: "column",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="stretch">
          <TextField
            label="ارسال پیام جدید"
            multiline
            minRows={2}
            value={outboxText}
            onChange={(e) => setOutboxText(e.target.value)}
            fullWidth
          />
          <IconButton
            color="primary"
            onClick={() => sendMutation.mutate()}
            disabled={
              sendMutation.isPending || !outboxText.trim() || !latestPhone
            }
            sx={{
              alignSelf: "stretch",
              width: 56,
              height: "100%",
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: (theme) =>
                sendMutation.isPending
                  ? theme.palette.action.disabledBackground
                  : theme.palette.background.paper,
              color: (theme) =>
                sendMutation.isPending
                  ? theme.palette.text.disabled
                  : theme.palette.primary.main,
              "&:hover": {
                bgcolor: (theme) =>
                  sendMutation.isPending
                    ? theme.palette.action.disabledBackground
                    : theme.palette.action.hover,
              },
              "& .MuiSvgIcon-root": {
                fontSize: 24,
              },
            }}
          >
            <SendIcon />
          </IconButton>
        </Stack>
        {!hideFilters && (
          <TextField
            label="شماره مقصد"
            value={latestPhone}
            disabled
            fullWidth
          />
        )}
        {sendMutation.isError && (
          <Typography variant="caption" color="error">
            {(sendMutation.error as Error)?.message}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {messages.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            نتیجه‌ای یافت نشد.
          </Typography>
        )}
        {messages.map((msg: SmsHistoryItem) => {
          const isInbound = msg.direction === 0;
          return (
            <Stack
              key={msg.id}
              direction="row"
              justifyContent={isInbound ? "flex-end" : "flex-start"}
            >
              <Paper
                elevation={2}
                sx={{
                  maxWidth: "75%",
                  p: 1.5,
                  bgcolor: isInbound
                    ? msg.isRead
                      ? "#ffffff"
                      : "#fff9c4" // زرد کمرنگ برای پیامک‌های خوانده نشده
                    : theme.palette.primary.main,
                  color: isInbound ? "text.primary" : "#fff",
                  borderRadius: 3,
                  position: "relative",
                }}
              >
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {!hideFilters && `${msg.phoneNumber} — `}
                  {new Date(msg.occurredOn).toLocaleString("fa-IR")}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                >
                  {msg.message}
                </Typography>
                {isInbound && !msg.isRead && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => markAsReadMutation.mutate(msg.id)}
                    disabled={markAsReadMutation.isPending}
                    sx={{
                      mt: 1,
                      fontSize: "0.75rem",
                      minWidth: "auto",
                      py: 0.5,
                    }}
                  >
                    خواندن
                  </Button>
                )}
                {isInbound && msg.isRead && msg.readByUserName && (
                  <Typography
                    variant="caption"
                    sx={{
                      opacity: 0.6,
                      display: "block",
                      mt: 0.5,
                      fontSize: "0.65rem",
                    }}
                  >
                    خوانده شده توسط: {msg.readByUserName}
                  </Typography>
                )}
                {msg.status && (
                  <Typography
                    variant="caption"
                    sx={{ opacity: 0.7, display: "block", mt: 0.5 }}
                  >
                    وضعیت: {statusLabel(msg.status)}
                    {msg.senderUserName
                      ? ` · فرستنده: ${msg.senderUserName}`
                      : msg.senderRef
                        ? ` · فرستنده: ${msg.senderRef}`
                        : ""}
                  </Typography>
                )}
                {msg.error && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    خطا: {msg.error}
                  </Typography>
                )}
              </Paper>
            </Stack>
          );
        })}
      </Box>
    </Box>
  );
};

export default SmsHistoryPage;
